import { ref, watch, computed, onMounted } from '@vue/composition-api'
import store from '@/store'
import useNotifications from '@/composables/useNotifications'
import { updateWorker, _getWorkers, getWorkersWithSupervisors } from '@/@core/queries/workers'
import { checkIfWorkerExistsInUser, updateUser } from '@/@core/queries/user'
import i18n from '@/libs/i18n'
import useCommon from '@/views/organization/useCommon'
import { useLambda } from '@/views/habit/awsLambda'
import { oid, date } from '@/helpers/mongoHelpers'
import useMongoDBFunctions from '@/views/habit/useMongoDBFunctions'

export default function useWorkerList() {
  const { showSuccessMessage, showErrorMessage } = useNotifications()
  const { handleError, getLocationsForDropDown, getPhysicalLocationsForDropDown, getRolesForDropDown, physical_locations, locations: bukLocations, roles: roleList } = useCommon()
  const { updateItem, getItems } = useLambda()
  const { workersQueryWithOffset } = useMongoDBFunctions()
  const { role, worker_id = null, client = null } = store.state?.userStore?.userData
  const workers = ref([])
  const collection = 'worker'

  // Table Headers
  const tableColumns = computed(() => {
    return [
      { key: 'name', label: i18n.t('message.tableHeader.name'), sortable: true },
      { key: 'privileges', label: i18n.t('label.Privileges'), sortable: true},
      { key: 'physicalLocationsNames', label: i18n.t('message.tableHeader.physical_locations'), sortable: true},
      { key: 'locationsNames', label: i18n.t('message.tableHeader.locations'), sortable: true},
      { key: 'email', label: i18n.t('message.tableHeader.email'), sortable: true },
      { key: 'roleNames', label: i18n.t('label.Roles'), sortable: true },
      { key: 'Actions', label: i18n.t('message.tableHeader.actions') },
    ]
  })
  const perPage = ref(10)
  const totalWorkers = ref(0)
  const currentPage = ref(1)
  const perPageOptions = [5, 10, 15, 20, 25, 30, 40, 50, 100]
  const searchQuery = ref('')
  const locationQuery = ref('')
  const locations = ref([])
  const defaultSortBy = 'name'
  const sortBy = ref(defaultSortBy)
  const isSortDirDesc = ref(false)
  const isLoading = ref(true)
  const locationFilter = ref([])
  const physicalLocationFilter = ref([])
  const roleFilter = ref([])
  const workersTotal = ref([])

  const excelFields = ref({
    [i18n.t('message.tableHeader.name')]: "name",
    [i18n.t('Email')]: "email",
    [i18n.t('label.Locations')]: "locationsData",
    [i18n.t('label.Physical Locations')]: "physicalLocationsData",
    [i18n.t('label.Privileges')]: "privileges",
    [i18n.t('label.Roles')]: "rolesData",
    [i18n.t('message.tableHeader.supervisor')]: "supervisorsData",
  })

  const fetchWorkers = async (searchValue = '') => {
    isLoading.value = true

    try {
      const input = {
        limit: perPage.value,
        total: currentPage.value === 1,
        offset: (currentPage.value * perPage.value) - perPage.value,
        query: {},
        search: searchQuery.value,
        sortBy: sortBy.value || defaultSortBy,
        sortOrder: isSortDirDesc.value ? -1 : 1,
        location: locationFilter.value,
        physical_location: physicalLocationFilter.value,
        roles: roleFilter.value,
        client_id: role !== 'admin' ? client.$oid : null,
        supervisor: ['supervisor', 'director'].includes(role) ? worker_id.$oid : null
      }
    
      const items = await workersQueryWithOffset(input)

      // Search
      if (searchValue && searchValue !== searchQuery.value) return

      workers.value = items?.workers || []
      workers.value?.forEach(w => {
        const translated = w.privileges ? i18n.t(`userRoles.${w.privileges}`) : ''
        w.privileges = translated.charAt(0).toUpperCase() + translated.slice(1)
      })
      if (currentPage.value === 1) totalWorkers.value = items?.totalRecords || 0

      // Add locationsNames, physicalLocationsNames and roleNames
      workers.value = workers.value?.map(worker => {
        const locationIds = worker.locations || []
        const physicalLocationIds = worker.physical_locations || []
        const roleIds = worker.roles || []

        // Match normal locations
        const locationsNames = Array.isArray(bukLocations?.value)
          ? bukLocations.value
              .filter(loc => Array.isArray(locationIds) && locationIds?.toString()?.includes(loc?.value))
              .map(loc => loc?.title)
          : []

        // Match physical locations
        const physicalLocationsNames = Array.isArray(physical_locations?.value)
          ? physical_locations.value
              .filter(loc => Array.isArray(physicalLocationIds) && physicalLocationIds?.toString()?.includes(loc?.value))
              .map(loc => loc?.title)
          : []

        // Match roles
        const roleNames = Array.isArray(roleList?.value)
          ? roleList.value
              .filter(role => Array.isArray(roleIds) && roleIds?.toString()?.includes(role?.value))
              .map(role => role?.title)
          : []

        return {
          ...worker,
          locationsNames: locationsNames || [],
          physicalLocationsNames: physicalLocationsNames || [],
          roleNames: roleNames || []
        }
      })

      const modifiedWorkers = items?.workersTotal?.map(worker => (
        {
          ...worker,
          locationsData: worker?.locationsData?.map(location => location.location).join(" / ") || "",
          physicalLocationsData: worker?.physicalLocationsData?.map(physicalLocation => physicalLocation.name).join(" / ") || "",
          rolesData: worker?.rolesData?.map(role => role.name).join(" / ") || "",
          supervisorsData: worker?.supervisorsData?.map(supervisor => supervisor.name).join(" / ") || ""
        }
      ))
      workersTotal.value = modifiedWorkers
      
    } catch (error) {
      console.log(error)
      handleError({ error, defaultMessage: i18n.t('message.err_worker_list') })
    } finally {
      isLoading.value = false
    }
  }

  const deleteWorker = async (id) => {
    try {
      const query = { _id: oid(id) }
      const action = { $set: { deleted: true, deletedBy: oid(worker_id.$oid), deletedAt: date(new Date()) } }

      await updateItem({ collection, query, action })

      // Check if worker is an existing user and soft delete it
      const userQuery = { 'custom_data.worker_id': oid(id) }
      await updateItem({ collection: 'user', query: userQuery, action })

      showSuccessMessage(i18n.t('message.worker_remove'))
      fetchWorkers()

      // Check if worker supervises other workers and remove it from the list of those workers' supervisors
      const supervisorQuery = { supervisors: oid(id) }
      const items = await getItems({ collection, query: supervisorQuery })

      const deletedWorkerStringId = id.toString()

      items?.forEach(worker => {
        if (worker?.supervisors?.length) {
          const supervisorsFiltered = worker.supervisors.filter(e => e.toString() !== deletedWorkerStringId)

          try {
            updateItem({ collection: 'worker', query: { _id: oid(worker._id) }, action: { $set: { supervisors: supervisorsFiltered?.map(e => oid(e)) } } })
          } catch (error) {
            console.log(error)
          }
        }
      })
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.something_went_wrong'))
    }
  }

  const updateFilter = (data) => {
    locationFilter.value = data.locationFilter
    roleFilter.value = data.roleFilter
    physicalLocationFilter.value = data.physicalLocationFilter
    fetchWorkers()
  }

  watch([currentPage, perPage], () => {
    fetchWorkers()
  })

  watch([locationQuery, searchQuery, sortBy, isSortDirDesc], () => {
    currentPage.value = 1
    fetchWorkers(searchQuery.value)
  })

  onMounted(async () => {
      getPhysicalLocationsForDropDown()
      getLocationsForDropDown()
      getRolesForDropDown()
  })

  return {
    fetchWorkers,
    tableColumns,
    perPage,
    currentPage,
    totalWorkers,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    workers,
    deleteWorker,
    locationQuery,
    locations,
    locationFilter,
    roleFilter,
    isLoading,
    role,
    excelFields,
    workersTotal,
    updateFilter
  }
}
